import { useState, useEffect, useMemo, useCallback } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { 
  Search, MapPin, Users, Target, RefreshCw, 
  SlidersHorizontal, Map, List, Building2, TrendingUp, Info
} from 'lucide-react';
import SEO from '../components/SEO';
import { Button } from '../components/ui/button';
import { Slider } from '../components/ui/slider';

const API = process.env.REACT_APP_BACKEND_URL;

// Party colors and labels (same as Election Odds)
const PARTY_CONFIG = {
  labour: { color: '#E4003B', label: 'Labour', shortLabel: 'LAB' },
  conservative: { color: '#0087DC', label: 'Conservative', shortLabel: 'CON' },
  libdem: { color: '#FAA61A', label: 'Lib Dem', shortLabel: 'LD' },
  reform: { color: '#12B6CF', label: 'Reform UK', shortLabel: 'REF' },
  green: { color: '#6AB023', label: 'Green', shortLabel: 'GRN' },
  snp: { color: '#FDF38E', label: 'SNP', shortLabel: 'SNP' },
  plaid: { color: '#005B54', label: 'Plaid Cymru', shortLabel: 'PC' },
  independent: { color: '#808080', label: 'Independent', shortLabel: 'IND' },
  other: { color: '#666666', label: 'Other', shortLabel: 'OTH' }
};

// Current national polling (same as MPs)
const DEFAULT_POLLING = {
  reform: 29,
  labour: 19,
  conservative: 18,
  green: 15,
  libdem: 12,
  snp: 3,
  plaid: 1,
  independent: 2,
  other: 1
};

// Baseline local election shares (2024 locals approximation)
const LOCAL_BASELINE = {
  labour: 34,
  conservative: 25,
  libdem: 18,
  green: 8,
  reform: 5,
  snp: 4,
  plaid: 1,
  independent: 4,
  other: 1
};

const CouncillorOddsPage = () => {
  const [councils, setCouncils] = useState([]);
  const [councillors, setCouncillors] = useState([]);
  const [polling, setPolling] = useState(DEFAULT_POLLING);
  const [customPolling, setCustomPolling] = useState(DEFAULT_POLLING);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterParty, setFilterParty] = useState('all');
  const [filterRegion, setFilterRegion] = useState('all');
  const [loading, setLoading] = useState(true);
  const [sortBy, setSortBy] = useState('name');
  const [viewMode, setViewMode] = useState('councils'); // councils, councillors
  const [showWhatIf, setShowWhatIf] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      // Fetch councils
      const councilsResponse = await axios.get(`${API}/api/councils?limit=500`);
      const councilsData = councilsResponse.data?.councils || [];
      
      // Calculate predictions for each council
      const predictedCouncils = councilsData.map(council => 
        calculateCouncilPrediction(council, customPolling)
      );
      setCouncils(predictedCouncils);

      // Fetch councillors summary
      const councillorsResponse = await axios.get(`${API}/api/councillors?limit=100`);
      setCouncillors(councillorsResponse.data?.councillors || []);

    } catch (error) {
      console.error('Error fetching data:', error);
    }
    setLoading(false);
  };

  // Calculate predicted seat changes for a council
  // Note: This is a SIMPLIFIED model. Real local elections depend on many local factors.
  // National polling swings are amplified in local elections due to first-past-the-post dynamics.
  const calculateCouncilPrediction = (council, currentPolling) => {
    const breakdown = council.party_breakdown || {};
    const totalSeats = council.total_seats || Object.values(breakdown).reduce((a, b) => a + b, 0);
    
    // Calculate swing from local baseline to current national polling
    const predictedSeats = {};
    let usedSeats = 0;
    
    const mainParties = ['labour', 'conservative', 'libdem', 'reform', 'green', 'snp', 'independent'];
    
    mainParties.forEach(party => {
      const currentSeats = breakdown[party] || 0;
      const baselineShare = LOCAL_BASELINE[party] || 0;
      const currentShare = currentPolling[party] || 0;
      const swing = currentShare - baselineShare;
      
      // Apply swing with amplification factor (local elections tend to amplify national swings)
      // Use a more aggressive model for significant swings
      const amplificationFactor = 2.5; // Local elections amplify national sentiment
      const effectiveSwing = swing * amplificationFactor;
      
      // For parties with seats, calculate based on swing
      let newSeats;
      if (currentSeats > 0) {
        // Negative swing: lose seats proportionally (with amplification)
        // Positive swing: gain seats (capped by available seats)
        const changeRatio = 1 + (effectiveSwing / 100);
        newSeats = Math.round(currentSeats * Math.max(0, changeRatio));
      } else {
        // Party has no seats - can they gain any?
        // Only if they have significant polling and there's vote share to capture
        if (currentShare > 10 && effectiveSwing > 20) {
          newSeats = Math.round(totalSeats * (effectiveSwing / 200));
        } else {
          newSeats = 0;
        }
      }
      
      newSeats = Math.max(0, newSeats);
      predictedSeats[party] = newSeats;
      usedSeats += newSeats;
    });
    
    // Handle "other" parties
    predictedSeats.other = Math.max(0, totalSeats - usedSeats);
    
    // Normalize to total seats
    const totalPredicted = Object.values(predictedSeats).reduce((a, b) => a + b, 0);
    if (totalPredicted > totalSeats) {
      const scale = totalSeats / totalPredicted;
      Object.keys(predictedSeats).forEach(party => {
        predictedSeats[party] = Math.round(predictedSeats[party] * scale);
      });
    }
    
    // Find current and predicted control
    const currentControl = Object.entries(breakdown)
      .sort(([,a], [,b]) => b - a)[0]?.[0] || 'other';
    const predictedControl = Object.entries(predictedSeats)
      .filter(([,v]) => v > 0)
      .sort(([,a], [,b]) => b - a)[0]?.[0] || currentControl;
    
    // Calculate net changes
    const changes = {};
    Object.keys({...breakdown, ...predictedSeats}).forEach(party => {
      const current = breakdown[party] || 0;
      const predicted = predictedSeats[party] || 0;
      changes[party] = predicted - current;
    });
    
    return {
      ...council,
      currentBreakdown: breakdown,
      predictedSeats,
      currentControl,
      predictedControl,
      isFlip: predictedControl !== currentControl,
      changes,
      totalSeats
    };
  };

  // Recalculate when polling changes
  useEffect(() => {
    if (showWhatIf && councils.length > 0) {
      const recalculated = councils.map(council => 
        calculateCouncilPrediction({
          ...council,
          party_breakdown: council.currentBreakdown
        }, customPolling)
      );
      setCouncils(recalculated);
    }
  }, [customPolling, showWhatIf]);

  // Get unique regions
  const regions = useMemo(() => {
    const regionSet = new Set(councils.map(c => c.region).filter(Boolean));
    return Array.from(regionSet).sort();
  }, [councils]);

  // Filter and sort councils
  const filteredCouncils = useMemo(() => {
    let filtered = councils;
    
    // Search
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(c => 
        c.name?.toLowerCase().includes(query) ||
        c.region?.toLowerCase().includes(query)
      );
    }
    
    // Party filter (councils where party has seats)
    if (filterParty !== 'all') {
      filtered = filtered.filter(c => 
        (c.predictedSeats?.[filterParty] || 0) > 0
      );
    }
    
    // Region filter
    if (filterRegion !== 'all') {
      filtered = filtered.filter(c => c.region === filterRegion);
    }
    
    // Sort
    filtered.sort((a, b) => {
      if (sortBy === 'name') return a.name?.localeCompare(b.name);
      if (sortBy === 'seats') return (b.totalSeats || 0) - (a.totalSeats || 0);
      if (sortBy === 'flips') return (b.isFlip ? 1 : 0) - (a.isFlip ? 1 : 0);
      return 0;
    });
    
    return filtered;
  }, [councils, searchQuery, filterParty, filterRegion, sortBy]);

  // Calculate summary stats
  const stats = useMemo(() => {
    const totalCouncils = councils.length;
    const flips = councils.filter(c => c.isFlip).length;
    
    // Count councils by predicted control
    const byControl = {};
    councils.forEach(c => {
      const control = c.predictedControl || 'other';
      byControl[control] = (byControl[control] || 0) + 1;
    });
    
    // Total seats by party
    const seatsByParty = {};
    councils.forEach(c => {
      Object.entries(c.predictedSeats || {}).forEach(([party, seats]) => {
        seatsByParty[party] = (seatsByParty[party] || 0) + seats;
      });
    });
    
    return { totalCouncils, flips, byControl, seatsByParty };
  }, [councils]);

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <RefreshCw className="h-8 w-8 animate-spin text-blue-600 mx-auto mb-4" />
          <p className="text-slate-600">Loading council data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 pb-20" data-testid="councillor-odds-page">
      <SEO 
        title="UK Council Election Predictions"
        description="See predicted outcomes for UK local council elections. Explore which parties could gain or lose control based on current polling."
        keywords="UK council elections, local elections, councillor predictions, council control, local government"
      />

      {/* Hero Section */}
      <section className="bg-gradient-to-br from-emerald-900 via-teal-900 to-slate-900 text-white py-10">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex items-center gap-3 mb-4">
            <div className="bg-emerald-500/20 p-2 rounded-xl">
              <Building2 className="h-6 w-6 text-emerald-400" />
            </div>
            <h1 className="text-2xl md:text-3xl font-black">Council Seats</h1>
          </div>
          
          {/* Info Box */}
          <div className="bg-amber-500/20 backdrop-blur rounded-lg p-3 mb-4 flex items-start gap-2 border border-amber-500/30">
            <Info className="h-5 w-5 text-amber-400 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-slate-300">
              <strong className="text-amber-300">Projection Model:</strong> Current seats show actual councillor data. 
              The <strong>"What If?"</strong> projections apply national polling swings to local seats with amplification 
              (local elections typically show larger swings than national polls). Based on current polling, 
              Labour is projected to lose significant seats while Reform UK gains substantially. 
              <em className="text-slate-400"> Actual results depend heavily on local factors, candidate quality, and turnout.</em>
            </div>
          </div>
          
          {/* Council Control Summary */}
          <div className="mt-6 grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
            {Object.entries(stats.byControl)
              .sort(([,a], [,b]) => b - a)
              .slice(0, 6)
              .map(([party, count]) => {
                const config = PARTY_CONFIG[party] || PARTY_CONFIG.other;
                return (
                  <div 
                    key={party}
                    className="bg-white/10 backdrop-blur rounded-xl p-3 text-center"
                  >
                    <div 
                      className="w-3 h-3 rounded-full mx-auto mb-1"
                      style={{ backgroundColor: config.color }}
                    />
                    <div className="text-xs text-slate-400">{config.shortLabel}</div>
                    <div className="text-2xl font-black">{count}</div>
                    <div className="text-xs text-slate-400">councils</div>
                  </div>
                );
              })}
          </div>
          
          {/* Total Seats by Party */}
          <div className="mt-4">
            <div className="text-xs text-slate-400 mb-2">Current Councillor Seats by Party</div>
            <div className="flex flex-wrap gap-3">
              {Object.entries(stats.seatsByParty)
                .sort(([,a], [,b]) => b - a)
                .slice(0, 8)
                .map(([party, seats]) => {
                  const config = PARTY_CONFIG[party] || PARTY_CONFIG.other;
                  return (
                    <div 
                      key={party}
                      className="flex items-center gap-2 bg-white/5 rounded-lg px-3 py-1.5"
                    >
                      <div 
                        className="w-2 h-2 rounded-full"
                        style={{ backgroundColor: config.color }}
                      />
                      <span className="text-sm text-slate-300">{config.shortLabel}</span>
                      <span className="text-sm font-bold text-white">{seats.toLocaleString()}</span>
                    </div>
                  );
                })}
            </div>
          </div>
          
          {/* Key Stats */}
          <div className="mt-6 flex flex-wrap gap-4">
            <div className="bg-white/10 backdrop-blur rounded-lg px-4 py-2">
              <span className="text-emerald-400 font-bold">{stats.totalCouncils}</span>
              <span className="text-slate-300 text-sm ml-2">Total Councils</span>
            </div>
            <div className="bg-white/10 backdrop-blur rounded-lg px-4 py-2">
              <span className="text-amber-400 font-bold">{showWhatIf ? stats.flips : 0}</span>
              <span className="text-slate-300 text-sm ml-2">{showWhatIf ? 'Projected Flips' : 'Using Current Data'}</span>
            </div>
            <Button
              variant="outline"
              onClick={() => setShowWhatIf(!showWhatIf)}
              className="ml-auto bg-emerald-500/20 border-emerald-500/50 text-emerald-300 hover:bg-emerald-500/30"
              data-testid="whatif-toggle"
            >
              <SlidersHorizontal className="h-4 w-4 mr-2" />
              {showWhatIf ? 'Hide' : 'What If?'}
            </Button>
          </div>

          {/* What If? Panel */}
          {showWhatIf && (
            <div className="mt-6 bg-white/10 backdrop-blur rounded-xl p-6" data-testid="whatif-panel">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-bold text-white flex items-center gap-2">
                  <SlidersHorizontal className="h-5 w-5" />
                  Adjust Polling - See Council Changes
                </h3>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setCustomPolling(polling)}
                  className="text-slate-300 hover:text-white"
                >
                  <RefreshCw className="h-4 w-4 mr-1" />
                  Reset
                </Button>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                {['reform', 'labour', 'conservative', 'libdem', 'green'].map(party => {
                  const config = PARTY_CONFIG[party];
                  return (
                    <div key={party} className="space-y-2">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <div 
                            className="w-3 h-3 rounded-full" 
                            style={{ backgroundColor: config.color }}
                          />
                          <span className="text-sm text-slate-300">{config.label}</span>
                        </div>
                        <span className="text-white font-bold">{customPolling[party]}%</span>
                      </div>
                      <Slider
                        value={[customPolling[party]]}
                        onValueChange={([value]) => setCustomPolling(prev => ({...prev, [party]: value}))}
                        min={0}
                        max={50}
                        step={1}
                        className="[&_[role=slider]]:bg-white"
                      />
                      <div className="text-xs text-slate-500">
                        Local baseline: {LOCAL_BASELINE[party]}%
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Search and Filters */}
      <section className="max-w-7xl mx-auto px-4 py-6">
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4">
          <div className="flex flex-col md:flex-row gap-4">
            {/* Search */}
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
              <input
                type="text"
                placeholder="Search council or region..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2.5 border border-slate-200 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                data-testid="council-search"
              />
            </div>
            
            {/* Region Filter */}
            <select
              value={filterRegion}
              onChange={(e) => setFilterRegion(e.target.value)}
              className="px-4 py-2.5 border border-slate-200 rounded-lg bg-white"
            >
              <option value="all">All Regions</option>
              {regions.map(region => (
                <option key={region} value={region}>{region}</option>
              ))}
            </select>
            
            {/* Party Filter */}
            <select
              value={filterParty}
              onChange={(e) => setFilterParty(e.target.value)}
              className="px-4 py-2.5 border border-slate-200 rounded-lg bg-white"
            >
              <option value="all">All Parties</option>
              {Object.entries(PARTY_CONFIG).map(([key, config]) => (
                <option key={key} value={key}>{config.label}</option>
              ))}
            </select>
            
            {/* Sort */}
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="px-4 py-2.5 border border-slate-200 rounded-lg bg-white"
            >
              <option value="name">Sort by Name</option>
              <option value="seats">Sort by Seats</option>
              <option value="flips">Sort by Flips</option>
            </select>
          </div>
          
          <div className="mt-3 text-sm text-slate-500">
            Showing {filteredCouncils.length} of {councils.length} councils
          </div>
        </div>
      </section>

      {/* Council List */}
      <section className="max-w-7xl mx-auto px-4 pb-10">
        <div className="grid gap-3">
          {filteredCouncils.slice(0, 50).map((council) => {
            const controlConfig = PARTY_CONFIG[council.predictedControl] || PARTY_CONFIG.other;
            const currentConfig = PARTY_CONFIG[council.currentControl] || PARTY_CONFIG.other;
            
            return (
              <Link
                to={`/council/${council.name?.toLowerCase().replace(/\s+/g, '-')}`}
                key={council.id}
                className={`bg-white rounded-xl border ${council.isFlip ? 'border-amber-300 shadow-md' : 'border-slate-200'} p-4 hover:shadow-lg transition-shadow block`}
                data-testid={`council-${council.id}`}
              >
                <div className="flex flex-col md:flex-row md:items-center gap-4">
                  {/* Council Info */}
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="font-bold text-lg">{council.name}</h3>
                      {council.isFlip && (
                        <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full font-medium">
                          FLIP
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-3 text-sm text-slate-500">
                      <span className="flex items-center gap-1">
                        <MapPin className="h-3.5 w-3.5" />
                        {council.region}
                      </span>
                      <span>{council.totalSeats} seats</span>
                    </div>
                  </div>
                  
                  {/* Current vs Predicted Control */}
                  <div className="flex items-center gap-4">
                    <div className="text-center">
                      <div className="text-xs text-slate-400 mb-1">Current</div>
                      <div 
                        className="px-3 py-1 rounded-full text-white text-sm font-bold"
                        style={{ backgroundColor: currentConfig.color }}
                      >
                        {currentConfig.shortLabel}
                      </div>
                    </div>
                    
                    <TrendingUp className="h-4 w-4 text-slate-400" />
                    
                    <div className="text-center">
                      <div className="text-xs text-slate-400 mb-1">Predicted</div>
                      <div 
                        className="px-3 py-1 rounded-full text-white text-sm font-bold"
                        style={{ backgroundColor: controlConfig.color }}
                      >
                        {controlConfig.shortLabel}
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* Seat Breakdown Bar */}
                <div className="mt-3 h-3 bg-slate-100 rounded-full overflow-hidden flex">
                  {Object.entries(council.predictedSeats || {})
                    .filter(([, seats]) => seats > 0)
                    .sort(([,a], [,b]) => b - a)
                    .map(([party, seats]) => {
                      const pct = (seats / council.totalSeats) * 100;
                      return (
                        <div
                          key={party}
                          className="h-full"
                          style={{ 
                            width: `${pct}%`, 
                            backgroundColor: PARTY_CONFIG[party]?.color || '#808080'
                          }}
                          title={`${PARTY_CONFIG[party]?.label}: ${seats} seats`}
                        />
                      );
                    })}
                </div>
                
                {/* Seat Numbers */}
                <div className="mt-2 flex flex-wrap gap-2">
                  {Object.entries(council.predictedSeats || {})
                    .filter(([, seats]) => seats > 0)
                    .sort(([,a], [,b]) => b - a)
                    .slice(0, 5)
                    .map(([party, seats]) => {
                      const change = council.changes?.[party] || 0;
                      const config = PARTY_CONFIG[party] || PARTY_CONFIG.other;
                      return (
                        <span key={party} className="text-xs flex items-center gap-1">
                          <span 
                            className="w-2 h-2 rounded-full" 
                            style={{ backgroundColor: config.color }}
                          />
                          <span className="font-medium">{seats}</span>
                          {change !== 0 && (
                            <span className={change > 0 ? 'text-green-600' : 'text-red-600'}>
                              ({change > 0 ? '+' : ''}{change})
                            </span>
                          )}
                        </span>
                      );
                    })}
                </div>
              </Link>
            );
          })}
        </div>
        
        {filteredCouncils.length > 50 && (
          <div className="text-center mt-6 text-slate-500">
            Showing first 50 results. Use search to find specific councils.
          </div>
        )}
      </section>

      {/* Methodology Note */}
      <section className="max-w-7xl mx-auto px-4 pb-10">
        <div className="bg-slate-100 rounded-xl p-6">
          <h3 className="font-bold mb-2">Methodology</h3>
          <p className="text-sm text-slate-600">
            Predictions use current council seat compositions and apply national polling swing to estimate changes. 
            Local factors, by-election results, and ward-level dynamics are not accounted for. 
            This is an indicative projection only.
          </p>
          <p className="text-xs text-slate-500 mt-2">
            Local baseline derived from 2024 local election results.
          </p>
        </div>
      </section>
    </div>
  );
};

export default CouncillorOddsPage;
